import { useState } from "react";
import { useComponentTranslation } from "@/services/i18n";
import toast from "react-hot-toast";
import useStore from "@/services/store";
import { FiCode, FiCopy, FiCheck } from "react-icons/fi";

const translations = {
  en: {
    title: "Iframe Integration",
    description: "Embed your job feed on external websites using an iframe",
    iframe_title: "Iframe Embed Code",
    iframe_help: "Copy and paste this code into your website's HTML to display the job feed",
    copy_iframe: "Copy Iframe Code",
    copied: "Copied!",
    preview_title: "Preview",
    preview_help: "This is how the iframe will appear on your website",
  },
  fr: {
    title: "Intégration Iframe",
    description: "Intégrez votre flux d'offres d'emploi sur des sites externes avec une iframe",
    iframe_title: "Code d'Intégration Iframe",
    iframe_help: "Copiez et collez ce code dans le HTML de votre site web pour afficher le flux d'emploi",
    copy_iframe: "Copier le Code Iframe",
    copied: "Copié !",
    preview_title: "Aperçu",
    preview_help: "Voici comment l'iframe apparaîtra sur votre site web",
  },
};

export default function IframeEmbed() {
  const { t } = useComponentTranslation(translations);
  const { organization } = useStore();
  const [copiedIframe, setCopiedIframe] = useState(false);

  const iframeUrl = `https://jobego.co/embed/${organization?.slug || "ecobank"}`;
  const iframeCode = `<iframe src="${iframeUrl}" width="100%" height="600" frameborder="0" scrolling="auto"></iframe>`;

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIframe(true);
      setTimeout(() => setCopiedIframe(false), 2000);
      toast.success(t("copied"));
    } catch (e) {
      toast.error(e.code);
    }
  };

  return (
    <div className="w-full">
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
            <FiCode className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{t("title")}</h2>
            <p className="text-sm text-gray-600 mt-1">{t("description")}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Iframe Code - 1/3 width */}
        <div className="lg:col-span-1 bg-white border border-gray-200 rounded-xl p-6">
          <label className="block text-sm font-semibold text-gray-900 mb-2">
            {t("iframe_title")}
          </label>
          <p className="text-sm text-gray-600 mb-4">{t("iframe_help")}</p>
          <div className="space-y-3">
            <div className="relative">
              <textarea
                value={iframeCode}
                readOnly
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 font-mono text-sm resize-none"
              />
            </div>
            <button
              onClick={() => copyToClipboard(iframeCode)}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors w-full justify-center"
            >
              {copiedIframe ? (
                <>
                  <FiCheck className="w-4 h-4" />
                  <span>{t("copied")}</span>
                </>
              ) : (
                <>
                  <FiCopy className="w-4 h-4" />
                  <span>{t("copy_iframe")}</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Right: Preview - 2/3 width */}
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl p-6">
          <label className="block text-sm font-semibold text-gray-900 mb-2">
            {t("preview_title")}
          </label>
          <p className="text-sm text-gray-600 mb-4">{t("preview_help")}</p>
          <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
            <iframe 
              src={iframeUrl} 
              width="100%" 
              height="600" 
              frameBorder="0" 
              scrolling="auto"
              className="w-full"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

